import wrapDisplayName from '@pkgs/shared-client/helpers/wrapDisplayName';
import cn from '@pkgs/shared-client/helpers/cn';
import { type ClassValue } from 'clsx';
import React, { useEffect, useRef } from 'react';

// Keep track of classes count so multiple usages of SVWithBodyClass with the same classes will not remove the classes once
// only one of them unmounts.
const BODY_CLASSES_COUNT: Record<string, number> = {};
const HTML_CLASSES_COUNT: Record<string, number> = {};

// Uses cn and breaks down parsed classes into an array
function getClassesArray(classes: ClassValue) {
	const parsedClasses = cn(classes);

	if (parsedClasses == null || parsedClasses == '') {
		return [];
	} else {
		return parsedClasses.split(' ');
	}
}

function toggleClasses(oldClasses: string[], newClasses: string[], applyToHTMLElement = false) {
	const toAdd = newClasses.filter((klass) => !oldClasses.includes(klass));
	const toDelete = oldClasses.filter((klass) => !newClasses.includes(klass));
	const element = applyToHTMLElement ? document.getElementsByTagName('html')[0] : document.body;

	const classesCount = applyToHTMLElement ? HTML_CLASSES_COUNT : BODY_CLASSES_COUNT;

	toAdd.forEach((klass) => {
		classesCount[klass] = (classesCount[klass] || 0) + 1;

		if (classesCount[klass] === 1) {
			element.classList.add(klass);
		}
	});
	toDelete.forEach((klass) => {
		classesCount[klass] = Math.max(0, (classesCount[klass] || 0) - 1);

		if (classesCount[klass] === 0) {
			element.classList.remove(klass);
		}
	});

	return newClasses;
}

function SVWithBodyClass<Props>(
	WrappedComponent: React.ComponentType<Props>,
	classesOrGetter: ClassValue | ((props: Props) => ClassValue),
	applyToHTMLElement = false,
) {
	let validatorFn: (props: Props) => ClassValue;

	if (typeof classesOrGetter === 'function') {
		validatorFn = classesOrGetter as (props: Props) => ClassValue;
	} else {
		const className = cn(classesOrGetter);
		validatorFn = () => className;
	}

	const SVWithBodyClassComponent = (props: Props): JSX.Element => {
		const classesRef = useRef<string[]>([]);

		useEffect(() => {
			classesRef.current = toggleClasses(
				classesRef.current,
				getClassesArray(validatorFn(props)),
				applyToHTMLElement,
			);

			return () => {
				// Remove classes
				classesRef.current = toggleClasses(classesRef.current, [], applyToHTMLElement);
			};
		}, [props]);

		// @ts-expect-error fine here
		return <WrappedComponent {...props} />;
	};

	SVWithBodyClassComponent.displayName = wrapDisplayName(WrappedComponent, 'SVWithBodyClass');

	return SVWithBodyClassComponent;
}

export default SVWithBodyClass;
